import "firebase/compat/firestore"
import "firebase/compat/auth"
import "firebase/compat/storage"
import firebase from "src/Firebase"
import { complexRef } from "src/services/complexServices"
import { store } from "src/redux/stores/store"
import axiosInstance from "src/utils/axios"
import randomHash from "src/utils/randomHash"
import { validateDailyReservationLimit } from "./dailyReservationLimit"

const db = firebase.firestore()

export const getAreasByConjunto = observer =>
  complexRef()
    .collection("areascomunales")
    .orderBy("Habilitado", "desc")
    .onSnapshot(observer)

export const getAreasHorasNoHabiles = (conjunto, areaID, observer) =>
  db
    .collection("conjuntos")
    .doc(conjunto)
    .collection("areascomunales")
    .doc(areaID)
    .collection("configuracion")
    .onSnapshot(observer)

export const updateHabilitado = (conjuntoID, areaID, estado) =>
  complexRef(conjuntoID).collection("areascomunales").doc(areaID).update({
    Habilitado: estado,
  })

export const deleteAreaById = (threadKey, userName) =>
  db
    .collection("conjuntos")
    .doc(threadKey)
    .collection("areascomunales")
    .doc(userName)
    .delete()

export const createQrArea = async (area, reserve) => {
  const { complex } = store.getState().complex
  await axiosInstance.post("qrcode/qr_area/", {
    conjunto: complex.id,
    code: reserve.Hash,
    direction: "in",
    date_from: reserve.Inicio,
    date_until: reserve.Fin,
    area: area.id,
    reserva: reserve.id,
    user: reserve.Usuario,
  })
}

export const newresv = async (area, reserva, cobros = null) => {
  const targetConjuntoId = area?.conjuntoId || area?.ConjuntoPrincipalId
  const areaRef = complexRef(targetConjuntoId)
    .collection("areascomunales")
    .doc(area.id)
  const areaSnapshot = await areaRef.get({ source: "server" })
  if (!areaSnapshot.exists) throw new Error("El area comunal ya no existe.")
  const currentArea = { ...area, ...areaSnapshot.data(), id: area.id }
  await validateDailyReservationLimit(areaRef, currentArea, reserva)
  const amount = Number(currentArea.ValorReserva || 0)
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error(
      "El valor por reserva del area no es valido. Revise su configuracion."
    )
  }
  const resRef = areaRef.collection("reservaciones").doc()
  if (area?.AllowQr) {
    reserva.Hash = randomHash(8)
    try {
      await createQrArea(area, { ...reserva, id: resRef.id })
    } catch (error) {
      reserva.Hash = ""
    }
  }
  const batch = db.batch()
  batch.set(resRef, reserva)
  if (cobros) {
    createReservationCharges({
      area: currentArea,
      reserva,
      ...cobros,
      batch,
      reservaId: resRef.id,
    })
  }
  return batch.commit()
}

export const updateresv = async (conjuntoID, area, reserva, data) => {
  if (data.Inicio || data.Usuario) {
    const areaRef = complexRef(conjuntoID)
      .collection("areascomunales")
      .doc(area.id)
    const snapshot = await areaRef.get({ source: "server" })
    if (!snapshot.exists) throw new Error("El area comunal ya no existe.")
    await validateDailyReservationLimit(
      areaRef,
      snapshot.data(),
      { ...reserva, ...data },
      reserva.id
    )
  }
  if (reserva.Hash && data.Inicio && data.Fin) {
    await axiosInstance
      .put(`qrcode/qr_area/update_code/${reserva.Hash}/`, {
        date_from: data.Inicio,
        date_until: data.Fin,
      })
      .catch(() => undefined)
  }
  if (!reserva.Hash && area?.AllowQr && data.Aprobada) {
    data.Hash = randomHash(8)
    try {
      await createQrArea(area, reserva)
    } catch (error) {
      data.Hash = ""
    }
  }
  await complexRef(conjuntoID)
    .collection("areascomunales")
    .doc(area.id)
    .collection("reservaciones")
    .doc(reserva.id)
    .update(data)
}

export const deleteresva = async (conjuntoID, idArea, idDoc, code = "") => {
  if (code) {
    await axiosInstance
      .delete(`qrcode/qr_area/delete_code/${code}/`)
      .catch(() => undefined)
  }
  await complexRef(conjuntoID)
    .collection("areascomunales")
    .doc(idArea)
    .collection("reservaciones")
    .doc(idDoc)
    .delete()
}

const createReservationCharges = ({
  area,
  user,
  reserva,
  generateGuarantee,
  batch,
  reservaId,
}) => {
  const charges = [
    {
      amount: generateGuarantee ? Number(area.Garantia) : 0,
      concepto: `Garantía por reserva de ${area.Nombre}`,
    },
    {
      amount: Number(area.ValorReserva || 0),
      concepto: `Valor por reserva de ${area.Nombre}`,
    },
  ].filter(charge => Number.isFinite(charge.amount) && charge.amount > 0)
  if (!charges.length) return
  if (!user?.idUser)
    throw new Error("No se encontro el residente para generar el cobro.")
  const { modules } = store.getState().complex
  const autoReserva = modules?.rubrosAutomaticos?.find(
    item => item.TipoDePago === "Reservas"
  )
  charges.forEach(({ amount, concepto }) => {
    const cuentaRef = complexRef().collection("cuentasPorCobrar").doc()
    batch.set(cuentaRef, {
      CasaUsuario: user.Casa,
      Descripcion: concepto,
      Nombre: concepto,
      FechaLimite: reserva.Inicio,
      FechaRegistro: new Date(),
      NombreUsuario: `${user.Nombre || ""} ${user.Apellido || ""}`.trim(),
      Rubro: autoReserva?.Rubro || "Ingresos extraordinarios",
      SubRubro: autoReserva?.SubRubro || "Reserva áreas comunales",
      UsuarioId: user.idUser,
      Valor: amount,
      ReservaId: reservaId,
      AreaId: area.id,
    })
  })
}

export const getConfigReserve = async (
  id,
  day,
  type = "Horas",
  conjuntoID = undefined
) => {
  const doc = await complexRef(conjuntoID)
    .collection("areascomunales")
    .doc(`${id}/configuracion/${day}`)
    .get()
  if (!doc.exists) {
    return { exists: false }
  }
  let config = doc.data()[type] || []
  if (type === "Bloques") {
    config = config.map((item, ind) => ({ ...item, id: ind }))
  }
  return {
    exists: config.length > 0,
    config,
  }
}
export const getAllConfigReserve = async (
  id,
  type = "Horas",
  conjuntoID = undefined
) => {
  const query = await complexRef(conjuntoID)
    .collection(`areascomunales/${id}/configuracion`)
    .get()
  if (query.empty) {
    return { exists: false, config: [] }
  }
  const config = query.docs.map(doc => ({
    [type]: doc.data()[type] || [],
    id: doc.id,
  }))
  return {
    exists: config.length > 0,
    config,
  }
}

export const areaReservesInRange = (
  id,
  start,
  observer,
  conjuntoID = undefined
) =>
  complexRef(conjuntoID)
    .collection("areascomunales")
    .doc(id)
    .collection("reservaciones")
    .where("Inicio", ">=", start)
    .onSnapshot(observer)

export const getAreasCompartidasRelacionados = async (
  idConjuntoActual,
  relacionados = []
) => {
  if (!Array.isArray(relacionados) || relacionados.length === 0) {
    return []
  }
  const idsRelacionados = relacionados.filter(
    id => Boolean(id) && id !== idConjuntoActual
  )
  if (idsRelacionados.length === 0) {
    return []
  }
  const areasPorConjunto = await Promise.all(
    idsRelacionados.map(async conjuntoId => {
      const snapshot = await db
        .collection("conjuntos")
        .doc(conjuntoId)
        .collection("areascomunales")
        .where("CompartirRelacionados", "==", true)
        .get()

      return snapshot.docs.map(doc => ({
        id: doc.id,
        conjuntoId,
        ...doc.data(),
      }))
    })
  )

  return areasPorConjunto.flat()
}
